import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

interface EmojiEntry {
  emoji: string;
  description: string;
}

const emojiDictionary: EmojiEntry[] = [
  {
    emoji: '😂',
    description: 'Вижу шутку, ставлю смайлик',
  },
  {
    emoji: '😢',
    description: 'просто выражение сожаления',
  },
  {
    emoji: '🗿',
    description: 'мега хорош',
  },
  {
    emoji: '🥴',
    description: 'выражение неудовлетворения',
  },
  {
    emoji: '🤔',
    description: 'Не знаю, что сказать, но делаю вид, что умный',
  },
  {
    emoji: '🤯',
    description: 'крайнее удивление',
  },
  {
    emoji: '💅',
    description: 'Какие мы нежные',
  },
  {
    emoji: '🔥',
    description: 'очевидно',
  },
  {
    emoji: '🎉',
    description: 'очевидно',
  },
  {
    emoji: '😱',
    description: 'очевидно',
  },
  {
    emoji: '👍',
    description: 'очевидно',
  },
  {
    emoji: '👌',
    description: 'очевидно',
  },
];

export function EmojiDictionary() {
  const [hoveredEmoji, setHoveredEmoji] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ left: 0, top: 0 });
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hoveredEmoji || !tooltipRef.current) return;

    const buttonEl = buttonRefs.current.get(hoveredEmoji);
    if (!buttonEl) return;

    const buttonRect = buttonEl.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 10;

    // Default: position above the button, centered
    let left = buttonRect.left + buttonRect.width / 2 - tooltipRect.width / 2;
    let top = buttonRect.top - tooltipRect.height - 8;

    // Check right boundary
    if (left + tooltipRect.width > viewportWidth - padding) {
      left = viewportWidth - tooltipRect.width - padding;
    }

    // Check left boundary
    if (left < padding) {
      left = padding;
    }

    // Check top boundary - if not enough space above, show below
    if (top < padding) {
      top = buttonRect.bottom + 8;
    }

    // Check bottom boundary
    if (top + tooltipRect.height > viewportHeight - padding) {
      top = viewportHeight - tooltipRect.height - padding;
    }

    setTooltipPosition({ left, top });
  }, [hoveredEmoji]);

  return (
    <div className="space-y-4">
      <p className="text-[#9B9A97] text-sm mb-4">
        Наведи на emoji, чтобы узнать его значение:
      </p>
      
      <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
        {emojiDictionary.map((entry) => (
          <div key={entry.emoji} className="relative">
            <motion.button
              ref={(el) => {
                if (el) buttonRefs.current.set(entry.emoji, el);
              }}
              onMouseEnter={() => setHoveredEmoji(entry.emoji)}
              onMouseLeave={() => setHoveredEmoji(null)}
              onFocus={() => setHoveredEmoji(entry.emoji)}
              onBlur={() => setHoveredEmoji(null)}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              className="text-3xl p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-help focus:outline-none focus:ring-2 focus:ring-white/20"
              aria-label={`${entry.emoji}: ${entry.description}`}
            >
              {entry.emoji}
            </motion.button>
          </div>
        ))}
      </div>

      {/* Fixed position tooltip - renders outside of flex container */}
      <AnimatePresence>
        {hoveredEmoji && (
          <motion.div
            ref={tooltipRef}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="fixed z-50 w-64 p-4 bg-[#2F2F2F] border border-[#404040] rounded-lg shadow-xl pointer-events-none"
            style={{
              left: tooltipPosition.left,
              top: tooltipPosition.top,
            }}
          >
            <div className="flex items-start gap-4">
              <span className="text-2xl flex-shrink-0">
                {emojiDictionary.find(e => e.emoji === hoveredEmoji)?.emoji}
              </span>
              <p className="text-white/80 text-sm leading-relaxed">
                {emojiDictionary.find(e => e.emoji === hoveredEmoji)?.description}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile: Selected emoji description */}
      <AnimatePresence mode="wait">
        {hoveredEmoji && (
          <motion.div
            key={hoveredEmoji}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="sm:hidden p-4 bg-[#2F2F2F] rounded-lg mt-4"
          >
            <div className="flex items-start gap-4">
              <span className="text-3xl">
                {emojiDictionary.find(e => e.emoji === hoveredEmoji)?.emoji}
              </span>
              <p className="text-white/80 text-sm">
                {emojiDictionary.find(e => e.emoji === hoveredEmoji)?.description}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
